import { deliverableStage } from '../utils/deliverables'
import type { Deliverable, Project, TemplateDeliverable } from '../types/portal'

/**
 * Small pure helpers shared by the Portal provider and its action builders.
 * Moved verbatim out of `contexts/PortalContext.tsx`.
 */

/** Stand-in used only before any project has loaded. */
export const EMPTY_PROJECT: Project = {
  id: '',
  slug: '',
  client: '',
  name: '',
  type: '',
  description: '',
  status: '',
  nextShootDate: '',
  nextShootTime: '',
  location: '',
  clientContact: '',
  producer: '',
  updated: '',
  deliverables: [],
  shoots: [],
  team: [],
  archived: false,
}

/** Date and time an action was taken, e.g. "26 Aug, 10:42 AM". */
export function clock(): string {
  const now = new Date()
  const date = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const time = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${date}, ${time}`
}

export function stamp(): string {
  return new Date()
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase()
}

let seq = 0
export function uid(prefix: string): string {
  seq += 1
  return `${prefix}-${Date.now()}-${seq}`
}

export function fromTemplate(row: TemplateDeliverable): Deliverable {
  return {
    id: uid('d'),
    name: row.name,
    type: row.type,
    quantity: '',
    /** New work always starts as Planned, whatever the template suggested. */
    status: 'Planned',
    version: '',
    versionDate: '—',
    runtime: '—',
    driveUrl: '',
    audioUrl: '',
    internalNote: '',
    visibleToClient: row.visibleToClient,
    notes: [],
    changeRequests: [],
    versions: [],
  }
}

export function versionLabel(status: Deliverable['status']): string {
  if (status === 'Changes Requested') return 'Changes Requested'
  if (status === 'Approved' || status === 'Delivered') return 'Approved'
  if (status === 'Ready for Review') return 'Reviewed'
  return 'First Cut'
}

export function isReleased(item: Deliverable): boolean {
  return (
    deliverableStage(item.status) === 'Final Delivery' &&
    item.visibleToClient &&
    item.status === 'Delivered'
  )
}

/**
 * Episode names are free text ("Episode 3", "03", "Episode Three", etc.), so
 * there's no dedicated order column to sort by — this reads the numbers
 * embedded in the name itself. `localeCompare` with `numeric: true` treats a
 * run of digits as one number rather than comparing them character by
 * character, so "Episode 2" sorts before "Episode 10" and "01"/"02"/"03"
 * land in the order they read, regardless of the order they were added in.
 */
export function byEpisodeName(a: Project, b: Project): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
}
